/**
 * `temporal.liveness` (ADR-0005 §"Auditor"; #197) — a cross-fact consistency
 * rule: an entity asserts a TERMINAL fact (a death / destruction —
 * "the queen died") yet still carries an OPEN, single-coverage segment that
 * implies the entity is doing something now ("…but she still reigns"). The
 * canonical example is a Character with a live `died_in` fact and a still-open
 * `ruled_by`-style `∅`-key segment whose validity never closed.
 *
 * Severity: `warning` — high editorial confidence the open segment should have
 * been closed at the death/destruction, but never refuses a write (only
 * `blocking_error` does; §5.1). The GM may dismiss it (e.g. a posthumous
 * regency that legitimately continues under the dead ruler's name).
 *
 * Why configurable terminal predicates: predicate identity is an OPAQUE id
 * (ADR-0004 §1) — there is no semantic `died_in`/`destroyed` flag on a
 * declaration, and the engine must stay English-free and pack-agnostic. So the
 * set of "this predicate marks the subject as gone" ids is passed in via
 * {@link AuditContext.liveness_terminal_predicates}; the client supplies the
 * official-core `died_in` id (and any homebrew terminals a GM later marks).
 * Absent / empty ⇒ the rule is a no-op (progressive disclosure — a campaign
 * with no terminal predicates configured never sees liveness advisories).
 *
 * What counts as "still open" (ADR-0005 §3–§4): a live, non-terminal segment
 * under an `∅`-uniqueness-key predicate (single coverage — "who holds this
 * role at time t") whose validity has NO `to` bound. An `∅`-key segment that
 * was already closed (a `validity.to` stamped — succession recorded it as
 * ended) is resolved history and is NOT flagged. A `{object}`/`{role}` key is
 * out of scope (those are sets/role-tuples, not a single live status).
 *
 * Owner-scoped: only CANON terminals close CANON segments. A belief that the
 * subject died does not contradict a canon open segment (different owners —
 * ADR-0005 §8); both terminal and open segment must be canon to fire.
 */
import { splitFactKey, uniquenessGroupValue, type FactState } from '@dl/shared';

import { factRef, type RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

/** The rule id — advisory `rule` + the §5.2.1 override key. */
export const TEMPORAL_LIVENESS_RULE = 'temporal.liveness';

/** Latest asserted value of a fact chain (respecting soft-delete). */
function currentValue(fact: FactState): unknown {
  if (fact.soft_deleted) return undefined;
  return fact.versions[fact.versions.length - 1]?.value;
}

export function temporalLiveness(ctx: AuditContext): RawAdvisory[] {
  const terminals = ctx.liveness_terminal_predicates;
  // No-op unless the campaign declares which predicates are terminal.
  if (!terminals || terminals.length === 0) return [];
  const terminalSet = new Set(terminals);

  const out: RawAdvisory[] = [];

  for (const entity of Object.values(ctx.entities)) {
    if (entity.soft_deleted) continue;

    // Find a live CANON terminal fact (death/destruction) on this entity.
    let terminalKey: string | null = null;
    for (const [factKey, fact] of Object.entries(entity.facts)) {
      if (fact.soft_deleted) continue;
      if (fact.owner.kind !== 'canon') continue;
      if (!terminalSet.has(splitFactKey(factKey).predicate)) continue;
      terminalKey = factKey;
      break;
    }
    if (terminalKey === null) continue;

    // Any live CANON, `∅`-key, still-OPEN segment is the contradiction.
    for (const [factKey, fact] of Object.entries(entity.facts)) {
      if (fact.soft_deleted) continue;
      if (fact.owner.kind !== 'canon') continue;
      const predicate = splitFactKey(factKey).predicate;
      if (terminalSet.has(predicate)) continue; // the terminal fact itself
      const decl = ctx.registry.lookup(predicate);
      if (!decl || decl.uniqueness_key === undefined) continue;
      // `∅`-key only (single live status). `{object}`/`{role}` keys are sets.
      if (uniquenessGroupValue(decl.uniqueness_key, currentValue(fact)) !== '') continue;
      // "Still open" = no closing `to` bound. A closed segment is ended history.
      if (fact.validity?.to !== undefined) continue;

      out.push({
        severity: 'warning',
        rule: TEMPORAL_LIVENESS_RULE,
        message_key: 'auditor.temporal.liveness',
        params: {
          predicate,
          terminal: splitFactKey(terminalKey).predicate,
        },
        fact_ids: [factRef(entity.id, factKey), factRef(entity.id, terminalKey)],
        entity_ids: [entity.id],
      });
    }
  }

  return out;
}
